import React from "react";
import {Button, InputFeedback, Spinner, TextareaField, TextField} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {FieldErrors, FieldPath, get, useFormContext, useWatch} from "react-hook-form";
import {emptyToNull, getInputValidationProps, ValidationRule} from "@/data/Validation";
import {PosOrderForm} from "@/data/entities/order";
import {
    addressValidation,
    OrderPlacementAddress,
    POSTAL_CODE_DIGITS_PATTERN,
    POSTAL_CODE_LETTERS_PATTERN,
} from "@/data/entities/deliveryAddress";
import {PostalCodeData} from "@/data/entities/postalCode";
import {usePostalCodeLookup} from "../../hooks/UsePostalCodeLookup";
import {label} from "@/utils/LabelUtils";

type Prefix = "deliveryAddress.address" | "businessDetails";
type AddressField = keyof OrderPlacementAddress;

type Props = {
    prefix: Prefix,
    // Whether street, number, postal code and city are required
    required: boolean,
    isPending: boolean,
    showName?: boolean,
};

const normalize = (value: string | null | undefined): string => (value ?? "").trim().toLowerCase();

/**
 * Address fields for a new delivery address or the business address. Empty
 * optional fields become `null`. After entering a postal code, the street and
 * city are suggested.
 */
const AddressFields = ({prefix, required, isPending, showName = false}: Props) => {
    const intl = useIntl();
    const {control, register, setValue, setFocus, formState: {errors}} = useFormContext<PosOrderForm>();
    const fieldErrors: FieldErrors<OrderPlacementAddress> | undefined = get(errors, prefix);
    const path = (field: AddressField) => `${prefix}.${field}` as FieldPath<PosOrderForm>;
    const registerField = (field: AddressField, pattern?: RegExp) => {
        const rule: ValidationRule = addressValidation[field];
        const isRequired = rule.required && required;
        return register(path(field), {
            ...getInputValidationProps({...rule, required: isRequired}, intl),
            pattern: pattern && {value: pattern, message: intl.formatMessage({id: "invalidPostalCode"})},
            // Fields of a new address are strings; the business address is optional, so blanks become null
            setValueAs: prefix === "deliveryAddress.address" && rule.required
                ? (value: string) => value.trim()
                : emptyToNull,
        });
    };
    // Suggest street & city once the postal code is complete
    const postalCodeDigits = useWatch({control, name: path("postalCodeDigits")}) as string | null | undefined;
    const postalCodeLetters = useWatch({control, name: path("postalCodeLetters")}) as string | null | undefined;
    const street = useWatch({control, name: path("street")}) as string | null | undefined;
    const city = useWatch({control, name: path("city")}) as string | null | undefined;
    const {
        data: postalCodeLookupData,
        isFetching: postalCodeLookupLoading,
        isError: postalCodeLookupFailed,
    } = usePostalCodeLookup(postalCodeDigits, postalCodeLetters);
    const suggestion: PostalCodeData | null = postalCodeLookupData?.[0] ?? null;
    // Lookup done, but no address: unknown postal code, or the API is unavailable
    const isSuggestionMissing: boolean = postalCodeLookupFailed
        || (postalCodeLookupData !== undefined && postalCodeLookupData.length === 0);
    const isSuggestionApplied: boolean = suggestion !== null
        && normalize(street) === normalize(suggestion.straat)
        && normalize(city) === normalize(suggestion.plaats);
    const applySuggestion = (data: PostalCodeData) => {
        const options = {shouldValidate: true, shouldDirty: true, shouldTouch: true};
        setValue(path("street"), data.straat, options);
        setValue(path("city"), data.plaats, options);
        setFocus(path("streetNumber"));
    };

    return (
        <div className={"w-full grid grid-cols-5 gap-4"}>
            {
                showName &&
                <TextField
                    containerClassname={"col-span-5"}
                    type={"text"}
                    label={label("addressName", false)}
                    placeholder={intl.formatMessage({id: "addressNamePlaceholder"})}
                    disabled={isPending}
                    error={fieldErrors?.name?.message}
                    {...registerField("name")}
                />
            }

            <TextField
                containerClassname={"col-span-3"}
                type={"text"}
                inputMode={"numeric"}
                label={label("postalCodeDigits", required)}
                tip={<FormattedMessage id={"postalCodeDigits.tip"}/>}
                placeholder={"6800"}
                maxLength={addressValidation.postalCodeDigits.maxLength}
                disabled={isPending}
                error={fieldErrors?.postalCodeDigits?.message}
                {...registerField("postalCodeDigits", POSTAL_CODE_DIGITS_PATTERN)}
            />

            <TextField
                containerClassname={"col-span-2"}
                type={"text"}
                label={label("postalCodeLetters", required)}
                tip={<FormattedMessage id={"postalCodeLetters.tip"}/>}
                placeholder={"AB"}
                maxLength={addressValidation.postalCodeLetters.maxLength}
                disabled={isPending}
                error={fieldErrors?.postalCodeLetters?.message}
                {...registerField("postalCodeLetters", POSTAL_CODE_LETTERS_PATTERN)}
            />

            <TextField
                containerClassname={"col-span-3"}
                type={"text"}
                label={label("street", required)}
                disabled={isPending}
                error={fieldErrors?.street?.message}
                {...registerField("street")}
                after={
                    postalCodeLookupLoading
                        ? (
                            <InputFeedback>
                                <Spinner size={"xs"} className={"inline-block"}/>
                                <FormattedMessage id={"postalCodeLookupLoading"}/>
                            </InputFeedback>
                        )
                        : (
                            isSuggestionMissing
                                ? (
                                    <InputFeedback>
                                        <FormattedMessage id={"postalCodeLookupNotFound"}/>
                                    </InputFeedback>
                                )
                                : (
                                    suggestion !== null && !isSuggestionApplied &&
                                    <Button
                                        variant={"link"}
                                        disabled={isPending}
                                        buttonAction={{
                                            as: "button",
                                            actionProps: {
                                                onClick: e => {
                                                    e.preventDefault();
                                                    applySuggestion(suggestion);
                                                },
                                            },
                                        }}
                                    >
                                        <FormattedMessage
                                            id={"postalCodeSuggestion"}
                                            values={{street: suggestion.straat, city: suggestion.plaats}}
                                        />
                                    </Button>
                                )
                        )
                }
            />
            <TextField
                containerClassname={"col-span-2"}
                type={"text"}
                label={label("streetNumber", required)}
                disabled={isPending}
                error={fieldErrors?.streetNumber?.message}
                {...registerField("streetNumber")}
            />
            <TextField
                containerClassname={"col-span-5"}
                type={"text"}
                label={label("city", required)}
                disabled={isPending}
                error={fieldErrors?.city?.message}
                {...registerField("city")}
            />

            <TextareaField
                containerClassname={"col-span-5"}
                rows={2}
                label={label("extraAddressInfo", false)}
                placeholder={intl.formatMessage({id: "extraAddressInfoPlaceholder"})}
                disabled={isPending}
                error={fieldErrors?.extraAddressInfo?.message}
                {...registerField("extraAddressInfo")}
            />
        </div>
    );
};

export default AddressFields;